import {
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { MdEdit, MdMoreVert, MdSettings } from "react-icons/md";
import { useTogglePlanStatus } from "../../hooks/useSubscribtionDashboard";
import PlanLoadingShimmer from "../../components/common/subscribtionShimmer/PlanLoadingShimmer";
import { ErrorRetry } from "../../components/common/ErrorRetry";
import { getErrorMessage } from "../../utils/error";

function PlansSubTable({
  plans,
  plansLoading,
  plansError,
  plansErrorObj,
  setEditMode = () => {},
  setDrawerOpen = () => {},
  setSelectedPlan = () => {},
  plansRefetch = () => {},
  page = 0,
  limit = 10,
  totalCount = 0,
  onPageChange = () => {},
  onLimitChange = () => {},
  onPlanClick,
  hideActions = false,
  selectedPlanId,
  onEditTeamPricing,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPlan, setMenuPlan] = useState(null);
  const { mutate } = useTogglePlanStatus();

  const transformedPlans = plans?.map((plan) => {
    const rawTeamPrice = plan.teamPrice ?? plan.pricePerTeamMember;
    const teamPrice =
      typeof rawTeamPrice === "number" ? rawTeamPrice : Number(rawTeamPrice);
    const monthly = plan.price > 0 ? plan.price : "Free";
    const annual =
      Number(plan.annualPrice) > 0
        ? Number(plan.annualPrice)
        : plan.price > 0
          ? plan.price * 11
          : "Free";

    return {
      id: plan._id,
      name: plan.name,
      description: plan.description,
      monthly: monthly,
      annual: annual,
      price: plan.price,
      annualPrice: plan.annualPrice,
      status: plan.isActive,
      color: plan.color,
      features: plan.features,
      privileges: plan.privileges,
      durationInDays: plan.durationInDays,
      initialSetupFee: plan.initialSetupFee,
      paystackId: plan.paystackId,
      annualPlanId: plan.annualPlanId,
      pricePerTeamMember: plan.pricePerTeamMember,
      teamPrice: Number.isFinite(teamPrice) ? teamPrice : 0,
      mode: plan.mode,
      fees: plan.fees,
      earnToPayMethod: plan.earnToPayMethod,
      earnToPayAmount: plan.earnToPayAmount,
      gracePeriodDays: plan.gracePeriodDays,
      trialDurationDays: plan.trialDurationDays,
    };
  });

  const handleStatusToggle = (planId) => {
    mutate({ planId });
  };

  const handleMenuOpen = (event, plan) => {
    setAnchorEl(event.currentTarget);
    setMenuPlan(plan);
    setSelectedPlan(plan);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPlan(null);
  };

  const handleEditPlan = () => {
    setEditMode(true);
    setDrawerOpen(true);
    handleMenuClose();
  };

  const handleEditTeamPricing = () => {
    if (!menuPlan || !onEditTeamPricing) return;
    onEditTeamPricing(menuPlan);
    handleMenuClose();
  };
  return (
    <>
      {plansLoading ? (
        <PlanLoadingShimmer rows={5} />
      ) : plansError ? (
        <ErrorRetry
          message={getErrorMessage(plansErrorObj)}
          onRetry={() => plansRefetch()}
        />
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
              <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                    Plan
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                    Monthly
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                    Annual
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                    Status
                  </TableCell>
                  {!hideActions && (
                    <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {transformedPlans?.map((plan) => (
                  <TableRow
                    key={plan.id}
                    onClick={() => onPlanClick?.(plan)}
                    sx={{
                      cursor: onPlanClick ? "pointer" : "default",
                      bgcolor:
                        selectedPlanId && selectedPlanId === plan.id
                          ? "#EEF2FF"
                          : "transparent",
                      "&:hover": {
                        bgcolor: "#F8FAFC",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography className="font-semibold text-slate-900">
                        {plan.name}
                      </Typography>
                      {plan.mode === "Free" && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#64748B", display: "block" }}
                        >
                          Trial: {plan.durationInDays} days
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-700">
                        {typeof plan.monthly === "number"
                          ? `₦${plan.monthly.toLocaleString()}`
                          : plan.monthly}
                      </Typography>
                      {plan.teamPrice > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#64748B", display: "block" }}
                        >
                          +₦{plan.teamPrice.toLocaleString()}/member
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-700">
                        {typeof plan.annual === "number"
                          ? `₦${plan.annual.toLocaleString()}`
                          : plan.annual}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        onClick={(e) => e.stopPropagation()}
                        control={
                          <Switch
                            checked={plan.status}
                            onChange={(e) => handleStatusToggle(plan.id)}
                            color="primary"
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#16A34A",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "#16A34A",
                                },
                            }}
                          />
                        }
                        label={plan.status ? "Active" : "Inactive"}
                        labelPlacement="end"
                        sx={{
                          margin: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: plan.status ? "#16A34A" : "#64748B",
                          },
                        }}
                      />
                    </TableCell>
                    {!hideActions && (
                      <TableCell>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, plan);
                          }}
                          size="medium"
                        >
                          <MdMoreVert />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/** pagination control */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(event, newPage) => onPageChange(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(event) => {
              const newLimit = parseInt(event.target.value, 10);
              onLimitChange(newLimit);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {!hideActions && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { borderRadius: "10px", minWidth: 160 },
          }}
        >
          <MenuItem onClick={handleEditPlan}>
            <MdEdit className="mr-2" />
            Edit Plan
          </MenuItem>
          {onEditTeamPricing && (
            <MenuItem onClick={handleEditTeamPricing}>
              <MdSettings className="mr-2" />
              Edit Team Pricing
            </MenuItem>
          )}
          {/* <MenuItem onClick={handleMenuClose} sx={{ color: "#DC2626" }}>
            <MdDelete className="mr-2" />
            Delete Plan
          </MenuItem> */}
        </Menu>
      )}
    </>
  );
}

export default PlansSubTable;
